/**
 * React Query wiring. Screens use these hooks and never call `useQuery` directly, so the
 * retry policy, the error type, and the toast-on-failure behaviour are decided once.
 *
 * DESIGN NOTES
 *   - Client errors are never retried. Retrying a 403 four times is four audit rows and
 *     zero chance of success; only network failures, 429 and 5xx get a second attempt.
 *   - `refetchOnWindowFocus` is ON. An operator who alt-tabs back to the console during an
 *     outage must not be reading a five-minute-old page.
 *   - Mutations report both outcomes as toasts (see Toast.tsx) unless the caller opts out
 *     because it is rendering the result inline.
 *   - `useMutate` supports optimistic updates WITH rollback. It is opt-in: an optimistic
 *     update on a screen whose server response reshapes the row is worse than a spinner.
 */
import { useCallback, useMemo } from 'react';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  QueryKey,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';

import { ApiError, api } from './api.ts';
import type { HelpTip, MeResponse, Page } from './api.ts';
import type { Permission, Role } from '../../../src/types.ts';
import { useToast } from '../components/Toast.tsx';
import type { ToastInput } from '../components/Toast.tsx';

// ------------------------------------------------------------------ query keys

/**
 * One key vocabulary for the whole console. A mutation on one screen invalidating a list
 * on another only works if both spell the key the same way, and free-form arrays in twelve
 * files do not stay spelled the same way.
 */
export const qk = {
  me: ['me'] as const,
  setup: ['setup'] as const,
  help: ['help'] as const,
  manual: ['manual'] as const,
  manualPage: (slug: string) => ['manual', slug] as const,
  tonight: ['tonight'] as const,
  calls: (params?: unknown) => ['calls', params ?? null] as const,
  call: (id: string) => ['call', id] as const,
  outages: ['outages'] as const,
  nms: ['nms'] as const,
  escalations: (params?: unknown) => ['escalations', params ?? null] as const,
  escalation: (id: string) => ['escalation', id] as const,
  proposals: (params?: unknown) => ['proposals', params ?? null] as const,
  inbox: (params?: unknown) => ['inbox', params ?? null] as const,
  policy: ['policy'] as const,
  playbooks: ['playbooks'] as const,
  integrations: ['integrations'] as const,
  billing: ['billing'] as const,
  reports: (days?: number) => ['reports', days ?? null] as const,
  users: ['users'] as const,
  brands: ['brands'] as const,
  numbers: ['numbers'] as const,
} as const;

/**
 * Keys that reflect "what is happening right now". A mutation that changes the night —
 * resolving an escalation, declaring an outage, deciding a proposal — invalidates these
 * so the status strip and the Tonight page stop disagreeing with the screen you are on.
 */
export const LIVE_KEYS: QueryKey[] = [qk.tonight, qk.outages];

// ------------------------------------------------------------------ client

function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && !error.retryable) return false;
  return failureCount < 2;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 6000),
        staleTime: 15_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
      },
      mutations: {
        // A mutation is a deliberate act. Repeating it automatically can double-book a
        // callback or send a second reminder; the operator retries, not the client.
        retry: false,
      },
    },
  });
}

// ------------------------------------------------------------------ session

export type Session = MeResponse;

/**
 * The console's identity call. Long staleTime because role and tenant do not change under
 * an operator mid-shift, but `refetchOnWindowFocus` still catches a session that expired
 * while the laptop was shut.
 */
export function useMe(options?: { enabled?: boolean }): UseQueryResult<MeResponse, ApiError> {
  return useQuery<MeResponse, ApiError>({
    queryKey: qk.me,
    queryFn: () => api.me(),
    staleTime: 60_000,
    retry: (count, error) => (error.status === 401 ? false : shouldRetry(count, error)),
  });
}

/** Non-throwing accessor for components that render with or without a session. */
export function useSession(): MeResponse | undefined {
  const { data } = useQuery<MeResponse, ApiError>({ queryKey: qk.me, queryFn: () => api.me(), enabled: false });
  return data;
}

export type PermissionCheck = {
  role: Role | null;
  permissions: ReadonlySet<Permission>;
  /** RBAC in the UI (doctrine 5): a control the role cannot use is not rendered. */
  can: (permission: Permission) => boolean;
  canAny: (...permissions: Permission[]) => boolean;
  canAll: (...permissions: Permission[]) => boolean;
};

export function usePermissions(): PermissionCheck {
  const session = useSession();
  return useMemo(() => {
    const set = new Set<Permission>(session?.permissions ?? []);
    const can = (permission: Permission): boolean => set.has(permission);
    return {
      role: session?.user.role ?? null,
      permissions: set,
      can,
      canAny: (...permissions: Permission[]) => permissions.some(can),
      canAll: (...permissions: Permission[]) => permissions.every(can),
    };
  }, [session]);
}

/** Sugar for the common single check: `const mayResolve = useCan('escalation.resolve')`. */
export function useCan(permission: Permission): boolean {
  return usePermissions().can(permission);
}

/**
 * Doctrine 7. True when EITHER the platform switch or the tenant switch is on — the UI
 * says "no outbound is escaping" in both cases, and the strip explains which one it is.
 */
export function useDemoMode(): { demo: boolean; platform: boolean; tenant: boolean } {
  const session = useSession();
  const platform = session?.platformDemoMode ?? false;
  const tenant = session?.tenant.demoMode ?? false;
  return { demo: platform || tenant, platform, tenant };
}

/** The tenant's IANA zone, for every timestamp the console renders. */
export function useTenantZone(): string | undefined {
  return useSession()?.tenant.timezone;
}

// ------------------------------------------------------------------ generic resource

export type ResourceOptions = {
  enabled?: boolean;
  /** Live screens (Tonight, Outage) poll; static ones do not. */
  refetchIntervalMs?: number | false;
  staleTimeMs?: number;
  /** Keeps the previous page visible while a filter change loads. */
  keepPreviousData?: boolean;
};

/**
 * The one read hook. Returns the raw react-query result — screens need `isLoading`,
 * `isError`, `error`, `refetch` and `isFetching` to render the four states the quality
 * bar requires (skeleton / empty / error+retry / data), so wrapping it further would
 * just hide them.
 */
export function useResource<T>(
  key: QueryKey,
  fetcher: () => Promise<T>,
  options: ResourceOptions = {},
): UseQueryResult<T, ApiError> {
  return useQuery<T, ApiError>({
    queryKey: key,
    queryFn: fetcher,
    enabled: options.enabled ?? true,
    ...(options.staleTimeMs === undefined ? {} : { staleTime: options.staleTimeMs }),
    ...(options.refetchIntervalMs === undefined
      ? {}
      : { refetchInterval: options.refetchIntervalMs === false ? false : options.refetchIntervalMs }),
    // react-query types placeholderData as NonFunctionGuard<T> to stop a caller passing a
    // function as the *value*. Our identity function is the documented keepPreviousData
    // idiom and is safe, but the guard cannot express "this function is a producer, not
    // the datum". Narrowed at this single line rather than loosening the hook's return
    // type, which is what every screen relies on for its four render states.
    ...(options.keepPreviousData
      ? { placeholderData: ((prev: T | undefined) => prev) as never }
      : {}),
  });
}

/** Lists all come back as { items, nextCursor }. This saves twelve `?.items ?? []`. */
export function itemsOf<T>(page: Page<T> | undefined): T[] {
  return page?.items ?? [];
}

// ------------------------------------------------------------------ mutations

export type MutateOptions<TData, TVars> = {
  mutationFn: (vars: TVars) => Promise<TData>;
  /** Toast on success. A string is the title; return a ToastInput for body/action. */
  success?: string | ((data: TData, vars: TVars) => string | ToastInput);
  /** Title above the server's own detail sentence. Defaults to "That did not go through". */
  errorTitle?: string | ((error: ApiError, vars: TVars) => string);
  /** Set when the screen renders the failure inline and a toast would be duplication. */
  silentError?: boolean;
  /** Keys to invalidate after settle — success or failure. */
  invalidate?: QueryKey[];
  /** Also invalidate Tonight + Outages, for anything that changes the night. */
  touchesTonight?: boolean;
  /**
   * Optimistic update with automatic rollback. `apply` receives the cached value for
   * `key` and returns the replacement. Deliberately untyped in the middle: the caller
   * knows the cache shape, and forcing a generic here made every call site cast anyway.
   */
  optimistic?: { key: QueryKey; apply: (previous: unknown, vars: TVars) => unknown };
  onSuccess?: (data: TData, vars: TVars) => void;
  onError?: (error: ApiError, vars: TVars) => void;
};

type OptimisticContext = { previous?: unknown; key?: QueryKey };

/**
 * The one write hook. Every mutation in the console goes through it, which is why every
 * mutation in the console has a toast, a rollback path, and a consistent error sentence.
 */
export function useMutate<TData, TVars = void>(
  options: MutateOptions<TData, TVars>,
): UseMutationResult<TData, ApiError, TVars, OptimisticContext> {
  const client = useQueryClient();
  const toast = useToast();

  const invalidateKeys = useCallback(
    async (): Promise<void> => {
      const keys = [...(options.invalidate ?? []), ...(options.touchesTonight ? LIVE_KEYS : [])];
      await Promise.all(keys.map((key) => client.invalidateQueries({ queryKey: key })));
    },
    // The key list is a literal at each call site; re-deriving it per render is cheaper
    // than asking every caller to memoise an array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, options.invalidate, options.touchesTonight],
  );

  return useMutation<TData, ApiError, TVars, OptimisticContext>({
    mutationFn: options.mutationFn,

    onMutate: async (vars) => {
      if (!options.optimistic) return {};
      const { key, apply } = options.optimistic;
      // Stop an in-flight refetch from landing on top of the optimistic value.
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData(key);
      client.setQueryData(key, (current: unknown) => apply(current, vars));
      return { previous, key };
    },

    onError: (error, vars, context) => {
      if (context?.key !== undefined) {
        // Rollback. The screen returns to the truth it had, and the toast explains why.
        client.setQueryData(context.key, context.previous);
      }
      if (!options.silentError) {
        const title =
          typeof options.errorTitle === 'function'
            ? options.errorTitle(error, vars)
            : (options.errorTitle ?? 'That did not go through');
        toast.push({ tone: 'danger', title, body: error.message });
      }
      options.onError?.(error, vars);
    },

    onSuccess: (data, vars) => {
      if (options.success) {
        const result = typeof options.success === 'function' ? options.success(data, vars) : options.success;
        toast.push(typeof result === 'string' ? { tone: 'ok', title: result } : { tone: 'ok', ...result });
      }
      options.onSuccess?.(data, vars);
    },

    onSettled: () => {
      void invalidateKeys();
    },
  });
}

// ------------------------------------------------------------------ help tips

/**
 * Doctrine 8. Every "?" in the console resolves from this one response, fetched once per
 * session and cached for the session — a help button must open instantly and must never
 * fail while the screen behind it is already on fire.
 */
export function useHelpTips(): UseQueryResult<Page<HelpTip>, ApiError> {
  return useQuery<Page<HelpTip>, ApiError>({
    queryKey: qk.help,
    queryFn: () => api.help(),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
}

/** Resolves one tip by its `page.anchor` key. Returns undefined until the fetch lands. */
export function useHelpTip(key: string): HelpTip | undefined {
  const { data } = useHelpTips();
  return useMemo(() => data?.items.find((t) => t.key === key), [data, key]);
}

export { ApiError };
